import { FC, ReactNode } from 'react';

import useToggleState from '@/hooks/useToggleState';

import { BUSINESS_TYPE } from '@/api/types';
import Cross from '@/assets/cross.svg?react';
import Plus from '@/assets/plus.svg?react';
import WarningIcon from '@/assets/warning.svg?react';
import BlockOverlayContent from '@/components/layouts/BlockOverlayContent';
import Modal, {
  ModalContent,
  ModalDescription,
  ModalTitle,
} from '@/components/ui/Modal';
import { useStepper } from '@/features/v2/lib/stepper';
import { getOpenText } from '@/helpers/getOpenText';
import { getTitle } from '@/helpers/getTitle';
import cn from '@/utils/cn';

import { PersonDataVehicleInputs } from './PersonDataVehicleInputs';
import { useLingui } from '@lingui/react';
import { useFieldArray, useFormState, useWatch } from 'react-hook-form';

interface PropsToggleSection {
  title: string;
  onConfirm: () => void;
  children: ReactNode;
  withoutDelete?: boolean;
  withModal?: boolean;
  info?: string;
  index: number;
}

const ToggleSection: FC<PropsToggleSection> = ({
  title,
  onConfirm,
  children,
  withoutDelete,
  withModal,
  info,
  index = 0,
}) => {
  const [dialogOpen, toggleDialogOpen] = useToggleState(false);
  const { i18n } = useLingui();

  return (
    <div className={cn(`h-full overflow-hidden duration-1000 ease-in-out`)}>
      <div className="mt-4 flex items-center justify-between gap-2">
        <p
          id={`add-coowner-title-${index}`}
          className="h4 overflow-initial relative scroll-mt-20 lg:scroll-mt-40"
        >
          {title}
        </p>
        {!withoutDelete && (
          <button
            type="button"
            className="cursor-pointer rounded bg-red-50 p-[7px] hover:bg-red-70"
            onClick={withModal ? toggleDialogOpen : onConfirm}
          >
            <Cross className="size-3.5 text-red-400" />
          </button>
        )}
      </div>
      {dialogOpen && withModal && (
        <Modal
          open
          onOpenChange={(open) => {
            if (!open) toggleDialogOpen();
          }}
        >
          <ModalContent className="max-w-[310px] gap-6 rounded-[28px] p-6 shadow-[0px_6px_16px_0px_rgba(0,0,0,0.16)] lg:max-w-[360px] lg:p-8">
            <BlockOverlayContent
              title={i18n.t('Potwierdź usunięcie danych')}
              buttonConfirm={{
                content: i18n.t('Tak, potwierdzam'),
                onClick: () => {
                  onConfirm();
                  toggleDialogOpen();
                },
              }}
              info={info}
              onClose={toggleDialogOpen}
              secondaryButtonContent={i18n.t('Anuluj')}
              icon={WarningIcon}
              titleAs={ModalTitle}
              descriptionAs={ModalDescription}
            />
          </ModalContent>
        </Modal>
      )}
      <div className="mt-3">{children}</div>
    </div>
  );
};

interface Props {
  recipientType: BUSINESS_TYPE | null;
  defaultValues: {
    phone: string;
    email: string;
    first_name: string;
    last_name: string;
  };
}

const AddCoowner: FC<Props> = ({ recipientType, defaultValues }) => {
  const { fields, append, remove } = useFieldArray({
    name: 'coowners',
  });

  const { errors } = useFormState({ name: 'coowners' });

  const isCompany = recipientType && recipientType !== BUSINESS_TYPE.JDG;
  const maxCoowners = 9;

  const handleAddCoowner = () => {
    if (fields.length < maxCoowners) {
      append({}, { shouldFocus: false });
      setTimeout(() => {
        const lastCoowner = document.getElementById(
          `add-coowner-title-${fields.length}`
        );
        lastCoowner?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 500);
    }
  };

  const handleRemoveCoowner = (index: number) => {
    remove(index);
  };

  const { progress } = useStepper();

  return (
    <section
      className={cn(
        isCompany
          ? 'w-full max-w-full space-y-4 rounded-[10px] border-t-[6px] border-blue-600 bg-gray-100 px-3.5 py-4 pt-2 md:space-y-5 md:rounded-lg md:p-6 md:pt-2'
          : 'mt-6 flex flex-col space-y-4 md:mt-1'
      )}
    >
      {isCompany && (
        <div className="flex flex-col">
          <p className="h4 mt-4">{getTitle(0, recipientType)}</p>
          <PersonDataVehicleInputs
            recipientType={recipientType}
            isCoOwner
            prefix={'owner'}
            defaultValues={defaultValues}
          />
        </div>
      )}
      {fields.map((field, index) => {
        return (
          <FieldComponent
            key={field.id}
            field={field}
            index={index}
            recipientType={recipientType}
            handleRemoveCoowner={handleRemoveCoowner}
            isCompany={!!isCompany}
          />
        );
      })}
      {fields.length < maxCoowners && (
        <button
          type="button"
          className="flex w-full cursor-pointer items-center justify-between gap-4 rounded-md border border-blue-600 bg-blue-200 px-3.5 py-2 text-blue-600 duration-150 ease-in-out hover:bg-blue-300 disabled:cursor-not-allowed disabled:border-dark-gray-300 disabled:bg-gray-500 disabled:text-dark-gray-300 lg:px-6 lg:py-2.5"
          onClick={handleAddCoowner}
          disabled={progress !== 100 || Object.keys(errors).length > 0}
          data-testid="add-coowner-button"
        >
          <p className="body text-left">
            {getOpenText(
              isCompany ? fields.length + 1 : fields.length,
              !!isCompany
            )}
          </p>
          <Plus className="size-4" />
        </button>
      )}
    </section>
  );
};

export default AddCoowner;

const FieldComponent = ({
  field,
  index,
  recipientType,
  handleRemoveCoowner,
  isCompany,
}: {
  field: Record<'id', string>;
  index: number;
  recipientType: BUSINESS_TYPE | null;
  handleRemoveCoowner: (index: number) => void;
  isCompany: boolean;
}) => {
  const { i18n } = useLingui();

  const watchValue = useWatch({
    name: `coowners.${index}`,
  });

  const isAnyFieldNotEmpty =
    watchValue &&
    Object.values(watchValue).some(
      (value) => !!value && value !== watchValue.id
    );

  return (
    <ToggleSection
      key={field.id}
      index={index}
      title={getTitle(isCompany ? index + 1 : index, recipientType)}
      onConfirm={() => handleRemoveCoowner(index)}
      withModal={isAnyFieldNotEmpty}
      info={
        isCompany
          ? i18n.t('Czy na pewno chcesz usunąć dane reprezentanta?')
          : i18n.t('Czy na pewno chcesz usunąć dane współwłaściciela?')
      }
    >
      <PersonDataVehicleInputs
        recipientType={recipientType}
        isCoOwner
        prefix={`coowners.${index}`}
        index={index}
        defaultValues={{
          phone: '',
          email: '',
          first_name: '',
          last_name: '',
        }}
      />
    </ToggleSection>
  );
};
